<template>
    <div>
        <span class="text-lg">
            Tình Trạng Pháp Lý Xe sẵn sàng cho việc vận chuyển hàng hóa container
        </span>
        <div class="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-8">
            <analytics-card
                :text="$t('labels.vehicleModule.ready')"
                :value="$formatters.formatCurrency(dataAnalytics.legalStatusContainerTransportReady)"
                color="success"
                increase
                @click="getItems('ready')" />
            <analytics-card
                :text="$t('labels.vehicleModule.notReady')"
                :value="$formatters.formatCurrency(dataAnalytics.legalStatusContainerTransportNotReady)"
                color="error"
                increase
                @click="getItems('notReady')" />
        </div>
    </div>

    <analytics-modal
        v-model:visible="visible"
        :headers="headers"
        :loading="loading"
        :items="items" />
</template>
<script lang="ts" setup>
import { CoveringContainerLevel, GoodsTypes } from '../../../../vehicle/ignore/imports/form'
import { formatTowingCapacity } from '../../../../vehicle/ignore/imports/utils'
import Status from '../../../../vehicle/ignore/status.vue'
import AnalyticsCard from '../analytics-card.vue'
import AnalyticsModal from '../analytics-modal.vue'
import { $api, $lodash } from '#imports'
const { $formatters } = useNuxtApp()
const dataAnalytics = ref<{ legalStatusContainerTransportReady: number; legalStatusContainerTransportNotReady: number }>({
  legalStatusContainerTransportReady: 0,
  legalStatusContainerTransportNotReady: 0
})

onMounted(async () => {
  const api = $url.dashboard.fleetVehicleManagement.getLegalStatusContainerTransport
  const data: any = await $api(api)
  const resData: any = $lodash.get(data, 'data.value')

  if (resData?.success) {
    dataAnalytics.value = resData.data
  }
})

const items = ref<Array<any>>([])
const store = $store()

const loading = ref(false)
const visible = ref(false)

const getItems = async (type: string) => {
  try {
    loading.value = true
    visible.value = true
    const api = $url.dashboard.fleetVehicleManagement.getDataLegalStatusContainerTransport
    const data: any = await $api(api, { uriParams: { TYPE: type } })
    const resData: any = $lodash.get(data, 'data.value')
    if (resData?.success) {
      items.value = resData.data
    }
    loading.value = false
  } catch (e: any) {
    loading.value = false
    visible.value = false
  }
}

const handleViewDetail = (vehicle: any) => {
  const routeData = useRouter().resolve({ name: 'trucker-id-vehicle-list', query: { action: 'view', idViewDetail: vehicle._id } })
  window.open(routeData.href, '_blank')
}

const headers = [
  {
    field: 'licensePlate',
    headerName: $t('labels.vehicleModule.licensePlate'),
    filter: 'agTextColumnFilter',
    sortable: true
  },
  {
    field: 'vehicleLoad',
    headerName: $t('labels.vehicleModule.type'),
    sortable: true,
    filter: 'agNumberColumnFilter',
    valueGetter: (params: any) => {
      if (!params || $lodash.isNil(params.data.vehicleLoad)) {
        return null
      }
      return getNameTypeVehicle(params.data.vehicleLoad)
    }
  },
  {
    field: 'towingCapacity',
    headerName: $t('labels.vehicleModule.containerCapacity'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.containerType) {
        return null
      }
      if (!params.data.towingCapacity || !params.data.towingCapacity.length) {
        return null
      }
      return formatTowingCapacity(params.data.towingCapacity)
    }
  },
  {
    field: 'isGenset',
    label: 'isGenset',
    headerName: $t('labels.vehicleModule.useGenset'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params || $lodash.isNil(params.data.isGenset)) {
        return null
      }
      if (params.data.isGenset) {
        return $t('labels.true')
      } else {
        return $t('labels.false')
      }
    }
  },
  {
    field: 'containerType',
    label: 'containerType',
    headerName: $t('labels.vehicleModule.containerType'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params || $lodash.isNil(params.data.containerType)) {
        return ''
      }
      if (params.data.containerType) {
        return $t('labels.vehicleModule.stickyContainer')
      } else {
        return $t('labels.vehicleModule.separatedContainer')
      }
    }
  },
  {
    field: 'coveringContainerLevel',
    label: 'coveringContainerLevel',
    headerName: $t('labels.vehicleModule.coveringContainerLevel'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      if (!params.data.coveringContainerLevel) {
        return ''
      }
      if (params.data.coveringContainerLevel === CoveringContainerLevel.PRIVATE) {
        return $t('labels.vehicleModule.private')
      } else if (params.data.coveringContainerLevel === CoveringContainerLevel.OPEN) {
        return $t('labels.vehicleModule.open')
      }
    }
  },
  {
    field: 'goodsTypes',
    label: 'goodsTypes',
    headerName: $t('labels.vehicleModule.goodsTypes'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params) {
        return null
      }
      const goodsType = params.data.goodsType
      if (goodsType.length > 0) {
        const formatGoodsType = goodsType.map((item: any) => {
          if (item === '') {
            return item
          }
          if (item === GoodsTypes.SOLID) {
            item = $t('labels.vehicleModule.solid')
          } else if (item === GoodsTypes.LIQUID) {
            item = $t('labels.vehicleModule.liquid')
          } else {
            item = $t('labels.vehicleModule.gas')
          }
          return item
        })
        return formatGoodsType.toString()
      }
    }
  },
  {
    field: 'price',
    headerName: $t('labels.vehicleModule.price'),
    sortable: true,
    filter: 'agNumberColumnFilter',
    valueGetter: (params: any) => {
      if (!params || $lodash.isNil(params.data.price)) {
        return null
      }
      return $formatters.formatCurrency(params.data.price)
    }
  },
  {
    field: 'driverName',
    headerName: $t('labels.driver'),
    sortable: true
  },
  {
    headerName: $t('labels.status'),
    cellRenderer: Status
  },
  {
    field: 'weight',
    headerName: $t('labels.vehicleModule.weight'),
    sortable: true,
    filter: 'agNumberColumnFilter',
    valueGetter: (params: any) => {
      if (!params || $lodash.isNil(params.data.weight)) {
        return null
      }
      return $formatters.formatCurrency(params.data.weight)
    }
  },
  {
    field: 'manufacture',
    filter: 'agTextColumnFilter',
    headerName: $t('labels.vehicleModule.manufacture')
  },
  {
    field: 'approvedByManagement',
    headerName: $t('labels.administration'),
    sortable: true,
    valueGetter: (params: any) => {
      const approved = params.data.approvedByManagement
      if (approved === true) {
        return $t('labels.confirmed')
      }
      if (approved === false) {
        return $t('labels.notYetConfirm')
      }
    }
  },
  {
    field: 'parkingNodeName',
    headerName: $t('labels.parkingLot')
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.createdAt) {
        return $formatters.datetime(params.data.createdAt)
      }
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: 'DataTableActions',
    cellRendererParams: {
      storeKey: 'dashboardDataTable',
      storeName: 'Dashboard',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          click: handleViewDetail,
          isAll: true
        }
      ]
    }
  }
]

const dataCategory: { [key: string]: any } = computed(() => {
  return store.data.getVehicleCategories
})

const getNameTypeVehicle = (vehicleLoad: number | undefined | null) => {
  if (typeof vehicleLoad === 'number') {
    let findCategory: any = null
    findCategory = dataCategory.value.find((item: { [key: string]: any }) => {
      if (item.from <= vehicleLoad && item.to >= vehicleLoad) {
        return true
      }
      return false
    })
    if (!findCategory) {
      findCategory = dataCategory.value.find((item: { [key: string]: any }) => {
        if (item.from <= vehicleLoad && $lodash.isNil(item.to)) {
          return true
        }
        return false
      })
    }
    return findCategory ? findCategory.name || '' : ''
  }
  return ''
}
</script>
